import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Button,
  TextField,
  useMediaQuery,
  useTheme,
  Grid,
  LinearProgress,
  Popover,
  IconButton
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getReportsAPI } from '../../lib/api';
import { colors } from '../../utils/colors';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';

const Reports = () => {
  const [reportsData, setReportsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [calendarView, setCalendarView] = useState('start'); // 'start' or 'end'
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const hasFetchedRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const endOfYear = new Date(now.getFullYear(), 11, 31);

    const startISO = startOfYear.toISOString().split('T')[0];
    const endISO = endOfYear.toISOString().split('T')[0];

    setStartDate(startISO);
    setEndDate(endISO);
    setTempStartDate(startISO);
    setTempEndDate(endISO);

    fetchReportsData(null, startISO, endISO);
  }, []);

  const fetchReportsData = async (status = null, start = null, end = null) => {
    try {
      setLoading(true);
      setError('');

      const finalStart = start ?? startDate;
      const finalEnd = end ?? endDate;

      const result = await getReportsAPI(status, finalStart, finalEnd);

      if (result.status) {
        setReportsData(result.data);
      } else {
        setError(result.message || 'Failed to fetch reports data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching reports data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    hasFetchedRef.current = false;
    fetchReportsData(status, startDate, endDate);
  };

  const handleApplyDateFilter = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setDatePickerAnchor(null);
    fetchReportsData(selectedStatus, tempStartDate, tempEndDate);
  };

  const handleDatePickerOpen = (event) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setDatePickerAnchor(event.currentTarget);
    setCalendarView('start');
    setCurrentMonth(new Date().getMonth());
    setCurrentYear(new Date().getFullYear());
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateSelect = (date) => {
    if (isFutureDate(date)) return;
    const dateStr = date.toISOString().split('T')[0];

    if (calendarView === 'start') {
      setTempStartDate(dateStr);
      // If selected start date is after current end date, update end date
      if (tempEndDate && new Date(dateStr) > new Date(tempEndDate)) {
        setTempEndDate(dateStr);
      }
      setCalendarView('end');
    } else {
      if (new Date(dateStr) < new Date(tempStartDate)) {
        // If end date is before start date, swap them
        setTempEndDate(tempStartDate);
        setTempStartDate(dateStr);
      } else {
        setTempEndDate(dateStr);
      }
    }
  };

  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    return compareDate > today;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    const actualAmount = parseFloat(amount) / 100;
    return `$${actualAmount.toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Cancelled':
        return 'error';
      case 'Non-subscribe':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    return status;
  };

  const formatPlanWithType = (subscription) => {
    const planName = subscription.Subscription?.name || 'N/A';
    const type = subscription.type === 'free' ? 'Trial' : subscription.type;

    if (subscription.display_status === 'Non-subscribe' || subscription.status === 'nonsubscribe') {
      return 'Non-subscribed';
    }

    if (planName.toLowerCase() === 'basic' && type.toLowerCase() === 'trial') {
      return `${planName} / ${type}`;
    }

    return planName;
  };

  const getTypeDisplayValue = (type) => {
    return type === 'free' ? 'Trial' : type;
  };

  const getTypeColor = (type) => {
    const displayType = getTypeDisplayValue(type);

    switch (displayType.toLowerCase()) {
      case 'trial':
        return {
          backgroundColor: colors.alpha.black10,
          color: colors.alpha.black70
        };
      case 'basic':
        return {
          backgroundColor: colors.infoLight || colors.primaryLight,
          color: colors.infoDark || colors.primaryDark
        };
      case 'premium':
        return {
          backgroundColor: colors.warningLight,
          color: colors.warningDark
        };
      case 'pro':
        return {
          backgroundColor: colors.successLight,
          color: colors.successDark
        };
      default:
        return {
          backgroundColor: colors.alpha.black10,
          color: colors.alpha.black70
        };
    }
  };

  // Calendar rendering functions
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth, currentYear);
    const today = new Date();
    const days = [];

    // Previous month days
    const prevMonthDays = getDaysInMonth(currentMonth - 1, currentYear);
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - 1, prevMonthDays - i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString()
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth, i);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString()
      });
    }

    // Next month days
    const totalCells = 42; // 6 weeks * 7 days
    const nextMonthDays = totalCells - days.length;
    for (let i = 1; i <= nextMonthDays; i++) {
      const date = new Date(currentYear, currentMonth + 1, i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString()
      });
    }

    return days;
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const isDateInRange = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);
    const current = new Date(dateStr);

    return current >= start && current <= end;
  };

  const isDateSelected = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return dateStr === tempStartDate || dateStr === tempEndDate;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <>
      {loading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 2001 }}>
          <LinearProgress
            sx={{
              '& .MuiLinearProgress-bar': {
                backgroundColor: colors.primary.main
              }
            }}
          />
        </Box>
      )}
      <Box sx={{
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        px: { xs: 1, sm: 2, md: 3 }
      }}>
        <PageBreadcrumbs
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Reports' }
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3, mx: { xs: 0, sm: 0 } }}>
            {error}
          </Alert>
        )}

        <Paper
          sx={{
            p: { xs: 1.5, sm: 3 },
            borderRadius: 2,
            boxShadow: 'none',
            border: `1px solid ${colors.alpha.black08}`,
            mb: 3,
            overflow: 'hidden',
            maxWidth: '100%',
            width: '100%'
          }}
        >
          <Box sx={{
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 3,
            width: '100%'
          }}>
            <Typography variant="h5" sx={{
              fontWeight: 600,
              textAlign: { xs: 'center', sm: 'left' },
              mb: { xs: 1, sm: 0 }
            }}>
              Subscriptions Reports
            </Typography>

            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              width: { xs: '100%', md: 'auto' }
            }}>
              <Box
                sx={{
                  display: 'flex',
                  width: { xs: '100%', sm: 'auto' },
                  backgroundColor: colors.common.white,
                  borderRadius: { xs: '8px', sm: '8px' },
                  border: `1px solid ${colors.border.main}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  position: 'relative',
                  minHeight: { xs: '44px', sm: '40px' }
                }}
              >
                {[
                  { value: null, label: 'ALL' },
                  { value: 'active', label: 'ACTIVE' },
                  { value: 'nonsubscribe', label: 'NON-SUBSCRIBE' },
                  { value: 'pending', label: 'PENDING' },
                  { value: 'cancelled', label: 'CANCELLED' }
                ].map((option, index, array) => {
                  const isSelected = selectedStatus === option.value;
                  const isFirst = index === 0;
                  const isLast = index === array.length - 1;
                  const prevSelected = index > 0 && selectedStatus === array[index - 1]?.value;

                  return (
                    <Box
                      key={option.value || 'all'}
                      onClick={() => handleStatusFilter(option.value)}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: isSelected ? colors.primary.main : colors.common.white,
                        color: isSelected ? colors.common.white : colors.primary.main,
                        fontWeight: isSelected ? 600 : 500,
                        whiteSpace: 'nowrap',
                        fontSize: {
                          xs: '0.7rem',
                          sm: '0.75rem',
                          md: '0.8125rem'
                        },
                        padding: {
                          xs: '12px 6px',
                          sm: '10px 10px',
                          md: '10px 16px'
                        },
                        minHeight: { xs: '44px', sm: '40px' },
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderRight: !isLast ? `1px solid ${isSelected && prevSelected ? colors.primary.main : isSelected ? colors.primary.main : colors.border.main}` : 'none',
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: isSelected ? colors.primary.hover : colors.background.hover,
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                        '@media (max-width: 600px)': {
                          minHeight: '44px',
                          padding: '12px 4px',
                          fontSize: '0.65rem',
                        },
                        '@media (min-width: 601px) and (max-width: 960px)': {
                          padding: '10px 8px',
                          fontSize: '0.7rem',
                        }
                      }}
                    >
                      {option.label}
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                width: { xs: '100%', sm: 'auto' },
                alignItems: { xs: 'stretch', sm: 'center' }
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  maxWidth: { xs: '100%', sm: 320 }
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flex: 1,
                    border: `1px solid ${colors.border.main}`,
                    borderRadius: '8px',
                    padding: '4px 12px',
                    minHeight: '40px',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: colors.primary.main,
                    }
                  }}
                    onClick={handleDatePickerOpen}
                  >
                    <CalendarTodayIcon sx={{
                      fontSize: 20,
                      color: colors.primary.main
                    }} />
                    <Typography variant="body2" sx={{
                      flex: 1,
                      fontSize: '0.8125rem',
                      color: colors.alpha.black70
                    }}>
                      {formatDate(startDate)} - {formatDate(endDate)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Date Range Picker Popover */}
          <Popover
            open={Boolean(datePickerAnchor)}
            anchorEl={datePickerAnchor}
            onClose={handleDatePickerClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 2,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                minWidth: 300,
                maxWidth: 350
              }
            }}
          >
            <Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                px: 1
              }}>
                <IconButton
                  size="small"
                  onClick={() => navigateMonth('prev')}
                  sx={{ p: 0.5 }}
                >
                  <Typography variant="body2">‹</Typography>
                </IconButton>

                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {monthNames[currentMonth]} {currentYear}
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => navigateMonth('next')}
                  sx={{ p: 0.5 }}
                >
                  <Typography variant="body2">›</Typography>
                </IconButton>
              </Box>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.5,
                mb: 1
              }}>
                {dayNames.map((day) => (
                  <Typography
                    key={day}
                    variant="caption"
                    align="center"
                    sx={{
                      fontWeight: 600,
                      color: colors.alpha.black60,
                      py: 0.5
                    }}
                  >
                    {day}
                  </Typography>
                ))}
              </Box>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.5
              }}>
                {renderCalendar().map((day, index) => {
                  const dateStr = day.date.toISOString().split('T')[0];
                  const isSelected = isDateSelected(day.date);
                  const isInRange = isDateInRange(day.date);
                  const isToday = day.isToday;
                  const isCurrentMonth = day.isCurrentMonth;
                  const isFuture = isFutureDate(day.date);

                  return (
                    <Box
                      key={index}
                      onClick={() => {
                        if (!isFuture) handleDateSelect(day.date);
                      }}
                      sx={{
                        aspectRatio: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        fontSize: '0.75rem',

                        cursor: isFuture ? 'not-allowed' : 'pointer',
                        pointerEvents: isFuture ? 'none' : 'auto',

                        backgroundColor: isSelected
                          ? colors.primary.main
                          : isInRange
                            ? colors.primary.light
                            : 'transparent',

                        color: isFuture
                          ? colors.alpha.black30
                          : isSelected
                            ? colors.common.white
                            : !isCurrentMonth
                              ? colors.alpha.black30
                              : colors.alpha.black70,

                        opacity: isFuture ? 0.4 : 1,

                        '&:hover': !isFuture && {
                          backgroundColor: isSelected
                            ? colors.primary.dark
                            : colors.primary.lighter,
                          color: colors.primary.main
                        },

                        ...(isToday && !isSelected && !isFuture && {
                          border: `1px solid ${colors.primary.main}`,
                          color: colors.primary.main
                        })
                      }}
                    >
                      {day.date.getDate()}
                    </Box>

                  );
                })}
              </Box>

              <Box sx={{
                mt: 2,
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end'
              }}>
                <Button
                  size="small"
                  onClick={handleDatePickerClose}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyDateFilter}
                  disabled={!tempStartDate || !tempEndDate}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem'
                  }}
                >
                  Apply Dates
                </Button>
              </Box>
            </Box>
          </Popover>

          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            position: 'relative',
            '&::-webkit-scrollbar': {
              height: '6px',
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: colors.alpha.black05,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors.alpha.black30,
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: colors.alpha.black50,
              }
            }
          }}>
            <Table sx={{
              minWidth: isMobile ? 800 : 600,
              width: '100%',
              tableLayout: 'auto'
            }}>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: colors.alpha.black05,
                  '& th': {
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                    py: { xs: 1.25, sm: 1.75 },
                    px: { xs: 1.5, sm: 2 },
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? 80 : 'auto',
                    borderBottom: `2px solid ${colors.alpha.black20}`
                  }
                }}>
                  <TableCell>#</TableCell>
                  <TableCell>User Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Plan Name</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {reportsData && reportsData.length > 0 ? (
                  reportsData.map((subscription, index) => (
                    <TableRow
                      key={subscription.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: colors.alpha.black02
                        },
                        '& td': {
                          py: { xs: 1.25, sm: 1.75 },
                          px: { xs: 1.5, sm: 2 },
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                          whiteSpace: 'nowrap',
                          minWidth: isMobile ? 120 : 'auto',
                          borderBottom: `1px solid ${colors.alpha.black10}`
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {subscription.User?.name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5
                        }}>
                          <Typography variant="body2" sx={{
                            fontSize: 'inherit',
                            color: colors.alpha.black70,
                            fontWeight: 500
                          }}>
                            + {subscription.User?.country_code} {subscription.User?.mobile_number}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                            fontWeight: 500,
                            color: colors.alpha.black90
                          }}
                        >
                          {formatPlanWithType(subscription)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ fontWeight: 600 }}>
                        {formatCurrency(subscription.amount || 0)}
                      </TableCell>

                      <TableCell>
                        {formatDate(subscription.start_date)}
                      </TableCell>
                      <TableCell>
                        {formatDate(subscription.end_date)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(subscription.display_status)}
                          color={getStatusColor(subscription.display_status)}
                          size="small"
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            height: { xs: 24, sm: 28 },
                            minWidth: 80,
                            fontWeight: 500,
                            '&.MuiChip-colorSuccess': {
                              backgroundColor: colors.successLight,
                              color: colors.successDark
                            },
                            '&.MuiChip-colorWarning': {
                              backgroundColor: colors.warningLight,
                              color: colors.warningDark
                            },
                            '&.MuiChip-colorError': {
                              backgroundColor: colors.errorLight,
                              color: colors.errorDark
                            },
                            '&.MuiChip-colorSecondary': {
                              backgroundColor: colors.greyLight || colors.alpha.black10,
                              color: colors.greyDark || colors.alpha.black70
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{
                      py: 6,
                      px: { xs: 1, sm: 2 }
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        No subscription reports available for the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {isMobile && !loading && reportsData && reportsData.length > 0 && (
            <Box sx={{
              mt: 2,
              textAlign: 'center',
              color: colors.alpha.black50,
              fontSize: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 1
            }}>
              <Typography variant="caption">
                ← Scroll horizontally to see more →
              </Typography>
            </Box>
          )}

          {!loading && reportsData && reportsData.length > 0 && (
            <Box sx={{
              mt: 3,
              pt: 2,
              borderTop: `1px solid ${colors.alpha.black10}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Typography variant="body2" color="text.secondary">
                Showing {reportsData.length} subscription{reportsData.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default Reports;